import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItemText, Menu, MenuItem, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { usePermissions } from '../../hooks/usePermissions';
import { fetchLeaveConfigs } from '../../store/hrmsStore/hrmsThunk';
import {
  approveEmployeeWeeklyTimesheet,
  cancelEmployeeLeaveRequest,
  clearEmployeeAttendance,
  fetchEmployeeLeaveRequests,
  fetchEmployeeWeeklyTimesheets,
  fetchProcessedSalaryForMonth,
  markEmployeeAttendance,
  markEmployeeLeaveOnBehalf,
} from '../../store/payrollStore/payrollThunk';

export interface AttendanceDayMenu {
  anchorEl: HTMLElement;
  date: string; // YYYY-MM-DD
  type: string; // resolved type shown on the calendar cell
  attendanceId?: string;
}

interface Props {
  employeeId: string;
  month: string; // YYYY-MM
  dayMenu: AttendanceDayMenu | null;
  onCloseDayMenu: () => void;
  // Attendance changed — the form reloads the month and recalculates salary.
  onChanged: () => void;
}

interface WeekRow {
  timesheetId: string;
  weekStartDate: string;
  weekEndDate: string;
  status: string;
}

const statusColor = (status: string) => {
  const s = status.toLowerCase();
  if (s === 'approved' || s === 'closed' || s === 'invoiced') return { bg: '#dcfce7', color: '#166534' };
  if (s === 'pending') return { bg: '#fef9c3', color: '#854d0e' };
  if (s === 'rejected') return { bg: '#fee2e2', color: '#991b1b' };
  return { bg: '#e5e7eb', color: '#374151' };
};

const isApproved = (status: string) => ['approved', 'closed', 'invoiced'].includes(String(status || '').toLowerCase());

const SalaryAttendanceEditor: FC<Props> = ({ employeeId, month, dayMenu, onCloseDayMenu, onChanged }) => {
  const dispatch = useAppDispatch();
  const { getUserRole } = usePermissions();
  const { selectedAccount, user: currentUser } = useAppSelector((state: any) => state.auth);

  const [weeks, setWeeks] = useState<WeekRow[]>([]);
  const [processedSalary, setProcessedSalary] = useState<any | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [leaveConfigs, setLeaveConfigs] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [weekBusyId, setWeekBusyId] = useState<string | null>(null);
  const [leaveDialogDate, setLeaveDialogDate] = useState<string | null>(null);
  const [leaveConfigId, setLeaveConfigId] = useState('');

  const loadState = useCallback(async () => {
    if (!employeeId || !month || !selectedAccount?._id) return;
    const monthStart = moment(`${month}-01`).startOf('month');
    const monthEnd = moment(monthStart).endOf('month');
    const [weekRes, salaryRes, leaveRes] = await Promise.allSettled([
      dispatch(fetchEmployeeWeeklyTimesheets({ employeeId, page: 1, limit: 200 })).unwrap(),
      dispatch(fetchProcessedSalaryForMonth({ accountId: selectedAccount._id, employeeId, month })).unwrap(),
      dispatch(fetchEmployeeLeaveRequests({ employeeId })).unwrap(),
    ]);
    const list: any[] = weekRes.status === 'fulfilled' && Array.isArray((weekRes.value as any)?.timesheets) ? (weekRes.value as any).timesheets : [];
    setWeeks(
      list
        .filter((w: any) => moment(w.weekEndDate).isSameOrAfter(monthStart, 'day') && moment(w.weekStartDate).isSameOrBefore(monthEnd, 'day'))
        .sort((a: any, b: any) => new Date(a.weekStartDate).getTime() - new Date(b.weekStartDate).getTime())
    );
    setProcessedSalary(salaryRes.status === 'fulfilled' ? salaryRes.value : null);
    setLeaveRequests(leaveRes.status === 'fulfilled' && Array.isArray(leaveRes.value) ? leaveRes.value : []);
  }, [dispatch, employeeId, month, selectedAccount?._id]);

  useEffect(() => { loadState(); }, [loadState]);

  const weekOf = useCallback((date: string) => weeks.find((w) =>
    moment(date).isSameOrAfter(moment(w.weekStartDate), 'day') && moment(date).isSameOrBefore(moment(w.weekEndDate), 'day')
  ), [weeks]);

  // Active (pending/approved) leave request covering a date.
  const leaveOn = useCallback((date: string) => leaveRequests.find((lr: any) =>
    ['approved', 'pending'].includes(String(lr.status || '').toLowerCase()) &&
    (Array.isArray(lr.dates) ? lr.dates : []).some((d: any) => String(d).slice(0, 10) === date)
  ), [leaveRequests]);

  // Why the selected day can't be edited, or '' when it can.
  const lockReason = useMemo(() => {
    if (!dayMenu) return '';
    if (processedSalary) return 'Salary is already processed for this month. Delete the salary slip to edit attendance.';
    if (moment(dayMenu.date).isAfter(moment(), 'day')) return 'Future dates cannot be marked.';
    const week = weekOf(dayMenu.date);
    if (week && isApproved(week.status)) return 'This week is approved. Reopen the week to edit it.';
    return '';
  }, [dayMenu, processedSalary, weekOf]);

  const afterChange = async (message: string) => {
    toast.success(message);
    await loadState();
    onChanged();
  };

  const run = async (action: () => Promise<any>, message: string) => {
    onCloseDayMenu();
    setBusy(true);
    try {
      await action();
      await afterChange(message);
    } catch (e: any) {
      toast.error(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleMark = (attendanceType: 'Present' | 'Absent' | 'Half-Day') => {
    if (!dayMenu) return;
    const { date } = dayMenu;
    run(
      () => dispatch(markEmployeeAttendance({ employeeId, accountId: selectedAccount._id, date, attendanceType, createdBy: currentUser?._id })).unwrap(),
      `${moment(date).format('DD MMM')} marked ${attendanceType}`
    );
  };

  const handleClear = () => {
    if (!dayMenu?.attendanceId) return;
    const { date, attendanceId } = dayMenu;
    run(() => dispatch(clearEmployeeAttendance({ attendanceId })).unwrap(), `${moment(date).format('DD MMM')} cleared`);
  };

  const handleCancelLeave = () => {
    if (!dayMenu) return;
    const request = leaveOn(dayMenu.date);
    if (!request) return;
    const dayCount = Array.isArray(request.dates) ? request.dates.length : 1;
    // Cancelling removes the whole request, not just this date.
    if (dayCount > 1 && !window.confirm(`This leave covers ${dayCount} days. Cancelling removes all of them. Continue?`)) {
      onCloseDayMenu();
      return;
    }
    run(() => dispatch(cancelEmployeeLeaveRequest({ requestId: request._id, employeeId })).unwrap(), 'Leave cancelled');
  };

  const openLeaveDialog = async () => {
    if (!dayMenu) return;
    setLeaveDialogDate(dayMenu.date);
    onCloseDayMenu();
    if (leaveConfigs.length === 0) {
      try {
        const res: any = await dispatch(fetchLeaveConfigs(selectedAccount._id)).unwrap();
        setLeaveConfigs(res?.data || res || []);
      } catch {
        setLeaveConfigs([]);
      }
    }
  };

  const submitLeave = async () => {
    if (!leaveDialogDate || !leaveConfigId || !currentUser?._id) return;
    const date = leaveDialogDate;
    setLeaveDialogDate(null);
    setBusy(true);
    try {
      await dispatch(markEmployeeLeaveOnBehalf({ employeeId, approvedBy: currentUser._id, leaveConfig: leaveConfigId, dates: [date] })).unwrap();
      await afterChange(`${moment(date).format('DD MMM')} marked On Leave`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to mark leave');
    } finally {
      setBusy(false);
      setLeaveConfigId('');
    }
  };

  const handleWeekAction = async (week: WeekRow, action: 'approve' | 'pending') => {
    if (!currentUser?._id) return;
    setWeekBusyId(week.timesheetId);
    try {
      await dispatch(approveEmployeeWeeklyTimesheet({
        approverId: currentUser._id, employeeId, timesheetId: week.timesheetId, action, userRole: getUserRole(),
      })).unwrap();
      await afterChange(action === 'approve' ? 'Week approved' : 'Week reopened');
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setWeekBusyId(null);
    }
  };

  const activeLeave = dayMenu ? leaveOn(dayMenu.date) : null;

  return (
    <Box sx={{ mb: 2 }}>
      {processedSalary ? (
        <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
          Salary is already processed for {moment(`${month}-01`).format('MMM YYYY')}, so attendance is locked. Delete the salary slip to edit and regenerate it.
        </Alert>
      ) : (
        <Typography sx={{ fontSize: 11, color: '#6b7280', mb: 1 }}>
          Click a day to mark Present, Half Day, Absent or On Leave.{busy && ' Saving…'}
        </Typography>
      )}

      {weeks.length > 0 && (
        <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
          {weeks.map((w, i) => {
            const c = statusColor(w.status || 'Open');
            const approved = isApproved(w.status);
            return (
              <Box key={w.timesheetId} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, bgcolor: '#fff', borderTop: i === 0 ? 'none' : '1px solid #f3f4f6' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#374151', flex: 1 }}>
                  {moment(w.weekStartDate).format('DD MMM')} – {moment(w.weekEndDate).format('DD MMM')}
                </Typography>
                <Box sx={{ px: 0.8, py: 0.2, borderRadius: 1, bgcolor: c.bg }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.color }}>{w.status || 'Open'}</Typography>
                </Box>
                <Button
                  size="small"
                  disabled={Boolean(processedSalary) || weekBusyId === w.timesheetId}
                  onClick={() => handleWeekAction(w, approved ? 'pending' : 'approve')}
                  sx={{ minWidth: 64, fontSize: 11, textTransform: 'none', py: 0 }}
                >
                  {weekBusyId === w.timesheetId ? <CircularProgress size={12} /> : approved ? 'Reopen' : 'Approve'}
                </Button>
              </Box>
            );
          })}
        </Box>
      )}

      <Menu anchorEl={dayMenu?.anchorEl || null} open={Boolean(dayMenu)} onClose={onCloseDayMenu}>
        <MenuItem disabled sx={{ opacity: '1 !important', py: 0.25 }}>
          <ListItemText
            primary={dayMenu ? moment(dayMenu.date).format('ddd, DD MMM YYYY') : ''}
            secondary={lockReason || (dayMenu?.type ? `Currently: ${dayMenu.type}` : 'No record')}
            primaryTypographyProps={{ fontSize: 12, fontWeight: 700 }}
            secondaryTypographyProps={{ fontSize: 11, color: lockReason ? '#b45309' : undefined, sx: { whiteSpace: 'normal', maxWidth: 240 } }}
          />
        </MenuItem>
        <Divider />
        {activeLeave ? (
          // The backend refuses attendance on a leave day, so the leave has to go first.
          <MenuItem disabled={Boolean(lockReason)} onClick={handleCancelLeave} sx={{ fontSize: 13 }}>Cancel leave</MenuItem>
        ) : ([
          <MenuItem key="p" disabled={Boolean(lockReason)} onClick={() => handleMark('Present')} sx={{ fontSize: 13 }}>Present</MenuItem>,
          <MenuItem key="hd" disabled={Boolean(lockReason)} onClick={() => handleMark('Half-Day')} sx={{ fontSize: 13 }}>Half Day</MenuItem>,
          <MenuItem key="a" disabled={Boolean(lockReason)} onClick={() => handleMark('Absent')} sx={{ fontSize: 13 }}>Absent</MenuItem>,
          <MenuItem key="l" disabled={Boolean(lockReason)} onClick={openLeaveDialog} sx={{ fontSize: 13 }}>On Leave…</MenuItem>,
          dayMenu?.attendanceId
            ? <MenuItem key="c" disabled={Boolean(lockReason)} onClick={handleClear} sx={{ fontSize: 13, color: '#c33b3b' }}>Reset this day</MenuItem>
            : null,
        ])}
      </Menu>

      <Dialog open={Boolean(leaveDialogDate)} onClose={() => setLeaveDialogDate(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>Mark On Leave — {leaveDialogDate ? moment(leaveDialogDate).format('DD MMM YYYY') : ''}</DialogTitle>
        <DialogContent>
          <TextField label="Leave Type" size="small" select fullWidth value={leaveConfigId} onChange={(e) => setLeaveConfigId(e.target.value)} sx={{ mt: 1 }}>
            {leaveConfigs.length === 0 && <MenuItem disabled value="">No leave types configured</MenuItem>}
            {leaveConfigs.map((lc: any) => (
              <MenuItem key={lc._id || lc.id} value={lc._id || lc.id}>{lc.name} ({lc.code})</MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 1 }}>The leave is saved as approved and deducted from the employee's balance.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveDialogDate(null)}>Cancel</Button>
          <Button variant="contained" disabled={!leaveConfigId} onClick={submitLeave}>Mark Leave</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalaryAttendanceEditor;
